import React, { useEffect, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import { Button, TextField, ButtonGroup, Backdrop } from '@mui/material';
import { login } from '../lib/apiRequests';
import { useDispatch } from 'react-redux';
import { loggedIn } from '../state/loginSlice';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import ActionButton from '../lib/ActionButton';

import PropTypes from 'prop-types';
import OAuthLoginButtons from './OAuthLoginButtons';
import SquareDotsSpinner from './SquareDotsSpinner';
import styles from './LoginDialog.module.css';

const loginImgPath = './placeholderLoginlogo.jpg';

// Same env var the MyJobs tab reads; kept out of the component so we
// don't re-evaluate window._env_ on every render. Empty string is fine —
// downstream paths will resolve relative to the current origin.
const apiUrl = window._env_.REACT_APP_WCIBAPI_URL || '';

// Friendly names for known OIDC relying parties — extend when new clients
// are registered. Lifted to module scope so describeNextDestination()
// AND isArgoSsoNext() share the exact same recognised-client list. If
// you add a non-Argo client here, also extend the prefix check inside
// isArgoSsoNext.
const knownClients = {
    'argo-staging': 'Argo Workflows (staging)',
    'argo-prod':    'Argo Workflows',
};

/**
 * Validate a `?next=` redirect target.
 *
 * Accepts:
 *   - same-origin absolute paths starting with '/'  e.g. "/api/v1/oidc/auth?..."
 *   - absolute URLs whose host matches one of the trusted hostnames the
 *     portal serves on (staging.wara-ops.org, staging.autodc.erdc.ericsson.net,
 *     portal.wara-ops.org, etc.)
 *
 * The OIDC authorize flow uses absolute URLs so the user lands back on the
 * exact host they came in on (the portal is multi-homed). Bare paths are still
 * accepted for backwards compatibility with anything else passing `next`.
 *
 * Blocks: javascript:/data: URIs, protocol-relative (//evil.com), and any
 * absolute URL whose origin is not a known portal host.
 *
 * IMPORTANT — encoding model:
 *   The caller passes the raw value from `URLSearchParams.get('next')`,
 *   which has ALREADY been percent-decoded ONCE by URLSearchParams. We
 *   therefore must NOT decodeURIComponent again — that would double-decode
 *   any percent-encoded characters inside the URL's own query string (e.g.
 *   the `%3A%2F%2F` in an OIDC `redirect_uri=https%3A%2F%2F...` parameter
 *   would be unescaped, corrupting the redirect_uri match downstream).
 *   The validation walks the value with no further decoding; the returned
 *   string is bit-identical to what URLSearchParams produced.
 *
 * Returns the sanitised string (path or absolute URL), or null if unsafe.
 */
function sanitiseNextParam(raw) {
    if (typeof raw !== 'string' || raw.length === 0) return null;

    // Same-origin absolute path
    if (raw.startsWith('/') && !raw.startsWith('//')) {
        // Reject anything that contains a scheme prefix before the first '/'.
        // Note: characters that happen to look like a scheme inside the
        // querystring are fine; only the very start of the value matters.
        if (/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(raw)) return null;
        return raw;
    }

    // Absolute URL — only allow if origin is one of the explicit allow-list of
    // known portal hosts. The list covers the "portal is reachable on multiple
    // DNS names" case (e.g. staging.wara-ops.org + staging.autodc.erdc.ericsson.net
    // both front the same wcibapi). Any URL outside this list is rejected.
    //
    // We use `new URL(raw)` ONLY to extract the protocol/host for the
    // allow-list check — we do NOT return `url.href` because URL normalises
    // some characters (e.g. encodes spaces, decodes some percent-escapes).
    // The original `raw` string is returned verbatim so the OIDC query
    // string is preserved bit-for-bit.
    let url;
    try {
        url = new URL(raw);
    } catch (_e) {
        return null;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    const allowedHosts = [
        window.location.host,
        // Hardcoded portal hosts — kept in sync with weborigin in the chart.
        'staging.wara-ops.org',
        'staging.autodc.erdc.ericsson.net',
        'portal.wara-ops.org',
        'frontend.wara-ops.org',
        'frontend.autodc.erdc.ericsson.net',
    ];
    if (allowedHosts.includes(url.host)) {
        return raw;
    }
    return null;
}

/**
 * Map a `?next=` URL to a short, human-friendly description of where the
 * user will end up after logging in. Shown above the login form so the
 * user understands why the dialog opened.
 *
 * The OIDC SSO flow puts a wcibapi authorize URL into `next=`, which
 * carries a `client_id` query param identifying the relying party.
 * That's what we key off here.
 *
 * Returns null when no useful description can be derived (e.g. `next=` is
 * a same-origin path with no recognised hint). Caller treats null as
 * "show no contextual notice" — keeps the dialog uncluttered for the
 * non-OIDC paths.
 */
function describeNextDestination(rawNextValue) {
    if (!rawNextValue) return null;

    let url;
    try {
        // Parse — works for absolute URLs (OIDC flow) and same-origin paths
        // (everything else). Use a base for the path-only case so URL parses.
        url = new URL(rawNextValue, window.location.origin);
    } catch (_e) {
        return null;
    }

    // Heuristic 1: OIDC authorize URL with a client_id we recognise.
    // Falls back to the raw client_id if unmapped.
    if (url.pathname.endsWith('/oidc/auth')) {
        const cid = url.searchParams.get('client_id');
        if (cid) return knownClients[cid] || cid;
    }

    // Heuristic 2: same-origin tab path — show which tab.
    if (url.pathname.startsWith('/tabselect/')) {
        const tab = url.pathname.split('/').pop();
        if (tab) return `the ${tab} tab`;
    }
    if (url.pathname === '/tabselect') return 'the dashboard';
    if (url.pathname === '/jwt') return 'the token generator';

    return null;
}

/**
 * Detect whether `next` is an OIDC authorize URL whose `client_id`
 * identifies the Argo Workflows relying party. The post-login flow
 * pre-provisions the user's Argo tenant namespace BEFORE jumping to
 * wcibapi `/v1/oidc/auth`, but only for Argo SSO destinations — other
 * OIDC clients (or same-origin paths) don't need it.
 *
 * Returns true iff:
 *   - `rawNextValue` parses as a URL,
 *   - the path ends with `/oidc/auth` (the wcibapi authorize endpoint),
 *   - and the `client_id` query param starts with "argo-" — matching
 *     every entry in the shared `knownClients` map and any future
 *     argo-* RP without requiring a code change here.
 *
 * Anything else (same-origin paths, unknown OIDC clients, garbage
 * input) returns false. Designed to never throw — caller treats false
 * as "skip pre-provision".
 */
function isArgoSsoNext(rawNextValue) {
    if (!rawNextValue) return false;

    let url;
    try {
        url = new URL(rawNextValue, window.location.origin);
    } catch (_e) {
        return false;
    }

    if (!url.pathname.endsWith('/oidc/auth')) return false;
    const cid = url.searchParams.get('client_id');
    if (!cid) return false;

    // Match the argo-* family by prefix rather than enumerating every
    // entry in knownClients — keeps adding a new tenant (`argo-foo`)
    // a one-line knownClients update, no second place to keep in sync.
    return cid.startsWith('argo-');
}

function LoginDialog(props) {
    const [open, setOpen] = useState(false);
    const [errorLogin, setErrorLogin] = useState(null);
    const [loginImg, setLoginImg] = useState(null);
    const [spinner, setSpinner] = useState(false);
    // Where to send the user after a successful login. A single string-valued
    // state covers both supported flows:
    //
    //   1. OIDC authorize round-trip — wcibapi redirects unauthenticated users
    //      to /home?next=<absolute-OIDC-url>. After login we do a full
    //      window.location.href jump back to the OIDC provider so the
    //      browser arrives with the WCIB session cookie attached.
    //
    //   2. In-app redirect-after-login — when the user navigates to
    //      /tabselect or /jwt while logged out, App.js's catch-all bounces
    //      them to /home with React Router state.login=true. We capture
    //      location.pathname here so we can navigate(next) back into the
    //      SPA after login (no full reload).
    //
    // Distinguishing the two at login time is purely structural: an
    // absolute URL → window.location.href; a same-origin path → navigate().
    const [next, setNext] = useState(null);
    // Post-login redirect interstitial state. Holds the human-readable
    // destination label (e.g. "Argo Workflows (staging)" or "continue") for
    // as long as the browser is mid-handoff to an absolute external URL.
    //
    // Rationale: when the absolute-URL branch of the after-login handler
    // fires `window.location.href = next`, React still gets one or two
    // frames before the browser actually navigates. During those frames:
    //   - the Redux loggedIn flip causes App.js to swap to the
    //     logged-in routes table, which has no `/home` route, so the
    //     catch-all `<NotFound>` card flashes.
    //   - the Dialog also closes, briefly exposing whatever is behind it.
    //
    // Setting `redirecting` to a truthy label renders a full-viewport
    // overlay ("Signing you in to <label>…" + spinner) that masks both
    // flickers and gives the user clear feedback that an SSO handoff is
    // in progress. Cleared only by the actual page navigation, so once
    // shown it stays shown until the browser leaves.
    const [redirecting, setRedirecting] = useState(null);
    // Sub-state of the redirect interstitial: which copy is shown above
    // the spinner. Two values:
    //
    //   'provisioning' — Argo SSO flow only. We've successfully logged in
    //       and are POSTing to /v1/argo/provision so the wcibapi
    //       OIDC handoff hits a warm cache (skips the 3-5 s synchronous
    //       ensureUserBundle() pause that otherwise leaves the browser
    //       on a blank wcibapi URL). Copy: "Setting up your Argo
    //       workspace…"
    //
    //   'redirecting'  — handing off to the destination via either
    //       window.location.href (Argo path, after provision succeeds /
    //       times out gracefully) or in-SPA navigate (recovery path on
    //       provision failure → /tabselect/MyJobs?argosetup=error).
    //       Copy: "Signing you in to <destination>…"
    //
    // The Backdrop's mount condition is `redirecting !== null` regardless
    // of stage, so the overlay never disappears between stages.
    const [redirectStage, setRedirectStage] = useState('redirecting');
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const handleClose = () => {
        if (open) {
            console.log('close dialog');
            setOpen(false);
            setValues({ ...values, password: '', username: '' });
        }
        postLoginHandleOngoing(false);
    };
    const handleClickOpen = () => {
        if (!open) {
            console.log('open dialog');
            setOpen(true);
        }
    };
    const [values, setValues] = useState({
        username: '',
        password: '',
        recaptchaToken: null,
    });
    const handleChange = (property) => (event) => {
        // console.log('onchange: ', event.target.value);
        setValues({ ...values, [property]: event.target.value });
    };

    // recaptcha
    // const [token, setToken] = useState();
    const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

    function closeit() {
        handleClose();
    }

    const postLoginHandleOngoing = (onGoing) => {
        setSpinner(onGoing);
    };

    function loginHandle() {
        // Add this to make autologin work
        if (values.username === '') {
            setErrorLogin('username needs to be set');
            return;
        }
        if (values.password === '') {
            setErrorLogin('password needs to be set');
            return;
        }
        setErrorLogin(null);
        postLoginHandleOngoing(true);
        login(values).then((user) => {
            // `user` is the payload returned by POST /v1/login (or the
            // legacy-shim for older API). Hand the whole object to Redux so
            // capability flags like canCreateDataset are available without a
            // follow-up /me call.
            dispatch(loggedIn(user));
            // After-login routing: one mechanism, two shapes.
            //   Absolute URL (e.g. OIDC authorize round-trip) → full browser
            //     navigation so the WCIB session cookie travels with the
            //     redirect and any cross-origin headers re-evaluate.
            //   Same-origin path (e.g. /tabselect, /jwt) → React Router
            //     navigate, no full reload.
            //   Nothing captured → default to the dashboard.
            if (next) {
                if (/^https?:\/\//i.test(next)) {
                    // Absolute URL handoff (OIDC SSO).
                    //
                    // Order of operations matters here:
                    //   1. Compute the friendly label and flip `redirecting`
                    //      ON. We deliberately DO NOT call
                    //      postLoginHandleOngoing(false) on this branch — we
                    //      want the dialog's spinner to stay on too, so the
                    //      Login button doesn't suddenly look re-enabled.
                    //   2. For the Argo SSO sub-flow, also pre-provision
                    //      the user's tenant namespace BEFORE the
                    //      window.location.href jump (see the
                    //      `if (isArgoSsoNext(next))` block below).
                    //   3. Defer the actual navigation to the next macrotask
                    //      (setTimeout 0). This guarantees React commits the
                    //      `redirecting` state — and therefore mounts the
                    //      Backdrop — BEFORE the browser starts navigating.
                    //      Without this gap, on slow paints we'd still see
                    //      the NotFound flash because window.location.href
                    //      is processed synchronously enough on some
                    //      browsers to beat the next paint.
                    //   4. Leave the dialog open and `redirecting` truthy.
                    //      The browser will tear the page down on its own
                    //      timeline; we don't have to clean up.
                    const label = describeNextDestination(next) || 'continue';

                    // Argo SSO — pre-provision so wcibapi /oidc/auth hits
                    // the warm cache path and 302s straight to Argo
                    // instead of running a 3-5 second synchronous
                    // ensureUserBundle() while the user stares at a blank
                    // wcibapi URL. Errors are non-fatal: the user is
                    // bounced to MyJobs?argosetup=error so the existing
                    // recovery UI takes over.
                    if (isArgoSsoNext(next)) {
                        provisionThenRedirect(next, label);
                        return;
                    }

                    // Non-Argo absolute redirect (any other future OIDC
                    // RP, or a future allow-listed external redirect).
                    // Behaviour unchanged: mount the interstitial and
                    // jump on the next tick.
                    setRedirectStage('redirecting');
                    setRedirecting(label);
                    setTimeout(() => {
                        window.location.href = next;
                    }, 0);
                    return;
                }
                postLoginHandleOngoing(false);
                navigate(next);
            } else {
                postLoginHandleOngoing(false);
                navigate('/tabselect');
            }
            handleClose();
        }).catch((err) => {
            if (err?.status) {
                if (err.status === 400) {
                    setErrorLogin('Are you a robot if not contact the support');
                } else if (err.status === 401) {
                    setErrorLogin('Bad password or username');
                } else if (err.status === 429) {
                    setErrorLogin('You\'ve made too many requests to the server, wait a while');
                } else if (err.status === 500) {
                    console.error('Login error: ', err);
                    setErrorLogin('Unknown error please, contact the support');
                } else {
                    console.error('Login error(2): ', err);
                    setErrorLogin('Unknown error please, contact the support');
                }
            } else {
                setErrorLogin('Couldn\'t reach the server');
            }
            setRefreshReCaptcha(!refreshReCaptcha);
            postLoginHandleOngoing(false);
        });
    }

    /**
     * Argo SSO post-login flow:
     *   1. Mount the interstitial in 'provisioning' stage.
     *   2. POST /v1/argo/provision with the freshly-issued session
     *      cookie. Wait up to 30 s — the wcibapi controller has its own
     *      ~30 s default timeout, so a shorter client-side timeout would
     *      just truncate a slow-but-succeeding provision.
     *   3. On 200 (or 503 = not configured in this env): swap copy to
     *      'redirecting' and let the browser jump to the OIDC URL.
     *      Wcibapi's /oidc/auth will see the warm cache and 302
     *      immediately, so the blank-page gap is gone.
     *   4. On any other error (network, 401, 5xx): SPA-navigate to
     *      /tabselect/MyJobs?argosetup=error. Same destination wcibapi
     *      itself uses, so the recovery UX is consistent. We do NOT
     *      jump to the OIDC URL — the user would just see the same
     *      provisioning error in wcibapi's path.
     *
     * The Backdrop is mounted before the await and never cleared —
     * either window.location.href tears the page down (success path),
     * or React Router navigate replaces the SPA tree (error path), and
     * the new MyJobs render carries its own UX.
     */
    function provisionThenRedirect(targetNext, label) {
        // TODO: REMOVE — debug logs for the OIDC SSO rollout, mirroring
        // the [LoginDialog]/[MyJobs] tagged style used elsewhere. Strip
        // once the flow is stable end-to-end.
        const dbg = (...args) => {
            if (process.env.NODE_ENV !== 'production') {
                console.log('[LoginDialog]', ...args);
            }
        };

        setRedirectStage('provisioning');
        setRedirecting(label);
        dbg('provisionThenRedirect: starting POST /v1/argo/provision', {
            targetNext,
            label,
        });

        axios.post(
            `${apiUrl}/v1/argo/provision`,
            {},
            { withCredentials: true, timeout: 30000 },
        ).then((res) => {
            dbg('provisionThenRedirect: provision OK', { status: res.status });
            // Success — swap copy and hand off via full-page nav.
            setRedirectStage('redirecting');
            setTimeout(() => {
                window.location.href = targetNext;
            }, 0);
        }).catch((err) => {
            const status = err?.response?.status;
            const errorCode = err?.response?.data?.error;
            const requestId = err?.response?.data?.requestId;

            // Structured 503 from wcibapi: artifact-storage (MinIO)
            // provisioning failed. The Argo namespace itself may be
            // half-set-up, but the chain is unrecoverable from
            // /v1/oidc/auth without a fresh attempt — sending the user
            // through the OIDC redirect would just hit the same error
            // again. Bounce them to MyJobs where the Alert above the
            // launch button explains what happened and offers a retry,
            // carrying the requestId so support can grep logs.
            if (status === 503 && errorCode === 'argo-s3-provision-failed') {
                console.error('[LoginDialog] argo-s3-provision-failed', {
                    requestId,
                    message: err?.response?.data?.message,
                });
                dbg('provisionThenRedirect: routing to MyJobs?argosetup=error&reason=s3', {
                    requestId,
                });
                setRedirectStage('redirecting');
                const params = new URLSearchParams({ argosetup: 'error', reason: 's3' });
                if (requestId) params.set('txid', requestId);
                navigate(`/tabselect/MyJobs?${params.toString()}`);
                return;
            }

            // Plain 503 = the chart in this environment doesn't have
            // Argo configured (no error body, or a different error
            // code). Treat as success so dev / mock environments
            // don't see a spurious error path; wcibapi's /oidc/auth
            // will respond on its own (success or its own 503), and
            // the user sees whatever the OIDC layer decides.
            if (status === 503) {
                dbg('provisionThenRedirect: 503 (Argo not configured) — proceeding anyway');
                setRedirectStage('redirecting');
                setTimeout(() => {
                    window.location.href = targetNext;
                }, 0);
                return;
            }

            // Any other failure: don't jump to wcibapi (the user would
            // just see the same provisioning error from there). SPA-
            // navigate to the MyJobs error UI instead. The Backdrop
            // stays mounted up to the navigate() call; React Router
            // replaces the route tree and MyJobs takes over rendering.
            console.error('[LoginDialog] /v1/argo/provision failed', {
                status,
                err: err?.message || err,
            });
            dbg('provisionThenRedirect: routing to MyJobs?argosetup=error');
            setRedirectStage('redirecting');
            // navigate replaces the route synchronously enough that the
            // Backdrop's last paint blends into MyJobs' first paint;
            // no setTimeout(0) needed here because we're not relying on
            // window.location to complete a cross-document navigation.
            navigate('/tabselect/MyJobs?argosetup=error');
        });
    }

    useEffect(() => {
        setErrorLogin(null);
    }, [open]);

    useEffect(() => {
        const photo = new Image();
        photo.src = loginImgPath;
        photo.onload = () => setLoginImg(photo.src);

        // Resolve the post-login destination. Two sources, in priority order:
        //
        //   1. ?next=<value> in the current URL — used by the OIDC authorize
        //      redirect (wcibapi sends an absolute URL) and by anything else
        //      that wants to deep-link the login dialog. sanitiseNextParam
        //      guards against open-redirect attacks (only same-origin paths
        //      and known-portal-host absolute URLs accepted).
        //
        //   2. The current location.pathname when the user landed on a
        //      protected SPA route while logged out — App.js's catch-all
        //      bounces them through /home with state.login=true, but the
        //      original pathname we want to return them to is lost by the
        //      time we get here. As a backstop, we capture location itself
        //      if its pathname looks like one of the protected routes.
        //
        // Whichever source produces a value, it's stored in the single
        // `next` state and the after-login handler dispatches based on
        // its shape (absolute URL → window.location.href, path → navigate).
        const queryParams = new URLSearchParams(location.search);
        const rawNext = queryParams.get('next');
        const safeNext = sanitiseNextParam(rawNext);
        if (safeNext) {
            setNext(safeNext);
            // Auto-open the dialog when ?next= is present so the user
            // doesn't have to click the Login button manually.
            handleClickOpen();
        } else if (next === null) {
            const from = location.pathname || '/home';
            if (from.startsWith('/tabselect') || from.startsWith('/jwt')) {
                setNext(from);
            }
        }

        if (location.state?.login && !open) {
            handleClickOpen();
        } else if (open && !safeNext) {
            handleClose();
        }
    }, [location]);

    // recaptcha
    const onVerify = useCallback((token) => {
        // setToken(token);
        setValues({ ...values, recaptchaToken: token });
        setTimeout(function () { setRefreshReCaptcha(r => !r); },
            100 * 1000); // timeout is 2 minutes
    }, []);

    // Human-readable description of where the user will end up after
    // login, derived from the captured `next` value. Shown as a contextual
    // notice above the form so the user understands why the dialog popped
    // up. Falls back to null (no notice) when there's no identifiable
    // destination — e.g. plain "Login" button click from the header.
    const destinationLabel = describeNextDestination(next);

    return (
        <React.Fragment >
            {props.onActivate(handleClickOpen)}
            <Dialog open={!!loginImg && open}
                onClose={handleClose}
                onKeyDown={(e) => { if (e.key === 'Enter') loginHandle(); }}>
                <div className={styles['login-frame']}>
                    <div className={styles['login-image-container']}>
                        <img src={loginImg} alt="Logo" height="100px" />
                    </div>
                    {destinationLabel && (
                        <div className={styles['login-context-notice']}>
                            <div>Sign in to continue to <b>{destinationLabel}</b>.</div>
                            {/*
                              * Only the Argo SSO branch involves a multi-step
                              * post-login handoff (provision → cross-domain
                              * redirect → wcibapi /oidc/auth → Argo). The
                              * Backdrop interstitial that fronts those steps
                              * passes too quickly to read on the warm path,
                              * so we surface the patience hint here, BEFORE
                              * the user clicks Login. Other recognised
                              * destinations (same-origin tab paths, the
                              * token generator) hand off in-SPA and don't
                              * need it.
                              */}
                            {isArgoSsoNext(next) && (
                                <div className={styles['login-context-notice-subtle']}>
                                    This may take a few seconds — please be patient while we hand off.
                                </div>
                            )}
                        </div>
                    )}
                    <div className={styles['login-input-group']}>
                        <div className={styles['login-error-space']}>{errorLogin}</div>
                        <div className={styles['login-dialog-input']}>
                            <TextField disabled={spinner} fullWidth required error={ !(values?.username) } label="Username" id="user" type="username" value={values.username} margin='dense' onInput={handleChange('username')} />
                        </div>
                        <div className={styles['login-dialog-input']}>
                            <TextField disabled={spinner} fullWidth required error={ !(values?.password) } label="Password" id="pass" type="password" value={values.password} margin='dense' onInput={handleChange('password')} />
                        </div>
                        <GoogleReCaptcha
                            onVerify={onVerify}
                            refreshReCaptcha={refreshReCaptcha}
                        />
                    </div>
                    <div className={styles['login-button-group']}>
                        <ButtonGroup>
                            {/*
                              * ActionButton (project-wide LoadingButton
                              * wrapper) defaults `loadingIndicator` to
                              * the WARA-Ops SquareDotsSpinner with
                              * aria-hidden, so the in-flight visual
                              * matches the redirect-interstitial
                              * Backdrop below and every other action
                              * surface in the app — no per-callsite
                              * override needed. The dots inherit
                              * currentcolor from the button's text
                              * colour, picking up the primary blue MUI
                              * used to render the ring spinner in.
                              */}
                            <ActionButton
                                loading={ spinner }
                                onClick={loginHandle}
                            >Login</ActionButton>
                            <Button disabled={ spinner } onClick={closeit}>Cancel</Button>
                        </ButtonGroup>
                    </div>
                    <OAuthLoginButtons />
                </div>
            </Dialog>
            {/*
              * Post-login redirect interstitial (OIDC SSO handoff).
              *
              * Rendered as a sibling of <Dialog> so it survives the dialog
              * closing and the App.js route table swap that happens the
              * moment Redux flips loggedIn=true. MUI Backdrop is fixed-
              * positioned at a high z-index, so it covers the header,
              * routes, and footer until the browser actually navigates.
              *
              * No close handler — the only exit is `window.location.href`
              * tearing the page down. Spinner + message gives the user
              * feedback that the SSO round-trip is in flight.
              */}
            <Backdrop
                open={!!redirecting}
                // Stack above MUI's Modal/Dialog (drawer is 1200, modal 1300,
                // tooltip 1500) so we cover the closing dialog too.
                sx={(t) => ({
                    zIndex: t.zIndex.tooltip + 1,
                    color: '#fff',
                    // Softer than Backdrop's default near-black so the
                    // portal's logo + branding still feel present.
                    backgroundColor: 'rgba(20, 24, 32, 0.82)',
                    backdropFilter: 'blur(2px)',
                })}
                role="status"
                aria-live="polite"
                aria-label={redirecting
                    ? (redirectStage === 'provisioning'
                        ? `Setting up your ${redirecting} workspace`
                        : `Signing you in to ${redirecting}`)
                    : undefined
                }
                data-testid="login-redirect-interstitial"
            >
                <div className={styles['redirect-interstitial-card']}>
                    {/*
                      * SquareDotsSpinner without its own label slot —
                      * the message below already carries the highlighted
                      * destination name (`<b>{redirecting}</b>` in the
                      * #cfe3ff blue family). Keeping the styled message
                      * as separate JSX preserves that emphasis, which
                      * the spinner's string-only `label` prop can't
                      * express. Inherits the Backdrop's white text
                      * colour via currentcolor on the dots.
                      *
                      * size="large" gives the same visual weight as the
                      * 56 px CircularProgress this replaced. We mark the
                      * spinner aria-hidden because the outer Backdrop
                      * already announces the redirect via its own
                      * role="status" + aria-label — without this, AT
                      * would announce "Loading" twice.
                      */}
                    <SquareDotsSpinner size="large" aria-hidden="true" />
                    {/*
                      * Two-stage copy. The Backdrop itself stays mounted
                      * across the swap (controlled by `redirecting`, not
                      * `redirectStage`), so the user sees a single
                      * continuous overlay whose text changes once when
                      * provisioning completes. Both lines emphasise the
                      * destination label with the same #cfe3ff accent —
                      * keeps the visual identity consistent.
                      */}
                    {redirectStage === 'provisioning' ? (
                        <>
                            <div className={styles['redirect-interstitial-message']}>
                                Setting up your <b>{redirecting}</b> workspace…
                            </div>
                            <div className={styles['redirect-interstitial-subtle']}>
                                This may take a few seconds — please be patient
                                while we hand off to the destination.
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles['redirect-interstitial-message']}>
                                Signing you in to <b>{redirecting}</b>…
                            </div>
                            <div className={styles['redirect-interstitial-subtle']}>
                                This may take a few seconds — please be patient
                                while we hand off to the destination.
                            </div>
                        </>
                    )}
                </div>
            </Backdrop>
        </React.Fragment>
    );
}

LoginDialog.propTypes = {
    onActivate: PropTypes.func.isRequired
};

// Exposed for unit testing — see LoginDialog.sanitiseNext.test.js.
// Not part of the public component API; do not import these from app code.
export {
    sanitiseNextParam as __sanitiseNextParam,
    isArgoSsoNext as __isArgoSsoNext,
};

export default LoginDialog;
